import React from "react";
import { Image, View, TouchableOpacity, StyleSheet } from "react-native";
import { useFieldArray, useForm, Controller } from "react-hook-form";
import {
  QuestionLayout,
  IconInput,
} from "@src/packages/medical-history/components";
import { useAllergies } from "../AllergiesContext";
import { useNavigation, type NavigationProp } from "@react-navigation/native";
import { FileText, Pill, Plus, X } from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import type { AllergiesStackParamList } from "../AllergiesNavigator";
import { DocumentRow } from "@louise/moon-ui";
import { EMBER, GLASS_CARD_STYLE } from "@louise/moon-ui/theme/tokens";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

const medicationPillIllustration =
  require("@src/apps/moon/assets/images/pill-capsule.png") as number;

type AllergiesForm = {
  allergies: {
    id?: string;
    name: string;
    comments: string;
  }[];
  toDelete: { id: string }[];
};

export function AllergiesDetailsScreen() {
  const t = useTranslations();
  const capturePress = usePressCapture("AllergiesDetailsScreen");
  const { state, updateState } = useAllergies();
  const navigation = useNavigation<NavigationProp<AllergiesStackParamList>>();
  const { control, handleSubmit, getValues, setValue } = useForm<AllergiesForm>(
    {
      defaultValues: {
        allergies:
          state.allergies && state.allergies.length > 0
            ? state.allergies.map((a) => ({
                id: a.id ?? "",
                name: a.name ?? "",
                comments: a.comments ?? "",
              }))
            : [{ name: "", comments: "" }],
        toDelete: [],
      },
      mode: "all",
    },
  );

  const { fields, append, remove } = useFieldArray({
    control,
    keyName: "_id",
    name: "allergies",
  });

  const onSubmit = (data: AllergiesForm) => {
    // Update state with form data
    const allergiesToKeep = data.allergies
      .filter((a) => a.name.trim() !== "")
      .map((a) => ({
        id: a.id,
        allergy: "medications" as const,
        name: a.name.trim(),
        comments: a.comments.trim() || null,
        other: null,
        isUnderTreatment: null,
        treatmentDescription: null,
      }));

    const allergiesToDelete = getValues("toDelete").map((d) => ({
      id: d.id,
      op: "delete" as const,
    }));

    updateState({
      allergies: [...allergiesToKeep, ...allergiesToDelete],
      allergyIdsToDelete: allergiesToDelete
        .map((d) => d.id)
        .filter((id): id is string => Boolean(id)),
    });

    // Navigate to complete screen
    navigation.navigate("AllergiesComplete");
  };

  return (
    <QuestionLayout
      title={t("med_history.medical.allergies.details_title")}
      subtitle={t("med_history.medical.allergies.details_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSubmit(onSubmit)}
      saveLabel={t("med_history.medical.shared.continue")}
    >
      <View style={styles.formContainer}>
        {fields.map((field, index) => (
          <View
            key={field._id}
            style={[GLASS_CARD_STYLE, styles.fieldContainer]}
          >
            {fields.length > 1 && (
              <View style={styles.fieldActions}>
                <TouchableOpacity
                  accessibilityLabel={t("med_history.medical.shared.remove")}
                  accessibilityRole="button"
                  onPress={() => {
                    capturePress(
                      pressLabel(t("med_history.medical.shared.remove")),
                      "med_history.medical.shared.remove",
                    );
                    if (field.id) {
                      setValue("toDelete", [
                        ...getValues("toDelete"),
                        { id: field.id },
                      ]);
                    }
                    remove(index);
                  }}
                  style={styles.removeButton}
                >
                  <X size={16} color="#ef4444" />
                </TouchableOpacity>
              </View>
            )}

            <View style={styles.inputGroup}>
              <Controller
                control={control}
                name={`allergies.${index}.name`}
                render={({ field: { onChange, value } }) => (
                  <IconInput
                    icon={Pill}
                    label={t(
                      "med_history.medical.medications_vaccines.medication_name_label",
                    )}
                    placeholder={t(
                      "med_history.medical.allergies.name_placeholder",
                    )}
                    value={value || ""}
                    onChangeText={onChange}
                  />
                )}
              />
            </View>

            <View style={styles.inputGroup}>
              <Controller
                control={control}
                name={`allergies.${index}.comments`}
                render={({ field: { onChange, value } }) => (
                  <IconInput
                    icon={FileText}
                    label={t(
                      "med_history.medical.medications_vaccines.comments_label",
                    )}
                    placeholder={t(
                      "med_history.medical.allergies.comments_placeholder",
                    )}
                    value={value || ""}
                    onChangeText={onChange}
                    multiline
                    numberOfLines={3}
                  />
                )}
              />
            </View>
          </View>
        ))}

        <DocumentRow
          onPress={() => append({ name: "", comments: "" })}
          accessibilityLabel={t("med_history.medical.allergies.add_another")}
          leading={
            <Image
              source={medicationPillIllustration}
              resizeMode="contain"
              style={styles.addIllustration}
            />
          }
          title={t("med_history.medical.allergies.add_another")}
          trailing={
            <Plus size={18} color={EMBER.colors.textLight} strokeWidth={1.5} />
          }
          showChevron={false}
          style={styles.addRow}
        />
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  formContainer: {
    width: "100%",
    marginBottom: 40,
  },
  fieldContainer: {
    marginBottom: 24,
    padding: 16,
    borderRadius: 16,
    borderWidth: 1,
  },
  fieldActions: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    minHeight: 24,
  },
  removeButton: {
    padding: 4,
  },
  inputGroup: {
    marginBottom: 12,
  },
  addRow: {
    width: "100%",
  },
  addIllustration: {
    width: 34,
    height: 34,
  },
});

export default AllergiesDetailsScreen;
